import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlanEingabe } from "@shared/plan";
import type { planLaden } from "../../worker/data/plan";
import { api, ApiFehler, rufe } from "../api/client";
import type { RegalZeile, Regalklasse, BuchungKurz, regalDetail, belegung, freieRegale } from "../../worker/data/regale";
import type { RegalEingabe, RegaleMassenEingabe, RegalklasseEingabe } from "@shared/schemas/stammdaten";

export type { RegalZeile, Regalklasse, BuchungKurz, RegalEingabe, RegalklasseEingabe };
export type RegalDetail = Awaited<ReturnType<typeof regalDetail>>;
export type Belegung = Awaited<ReturnType<typeof belegung>>;
export type FreieRegale = Awaited<ReturnType<typeof freieRegale>>;
export type KlasseZeile = Regalklasse & { anzahlRegale: number };

const K_REGALE = ["laden", "regale"] as const;
const K_KLASSEN = ["laden", "regalklassen"] as const;

function nachAenderung() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["laden", "regale"] });
    void qc.invalidateQueries({ queryKey: ["laden", "regalklassen"] });
    void qc.invalidateQueries({ queryKey: ["laden", "belegung"] });
    void qc.invalidateQueries({ queryKey: ["laden", "uebersicht"] });
    void qc.invalidateQueries({ queryKey: ["laden", "einrichtung"] });
  };
}

export const useRegalklassen = () => useQuery({ queryKey: K_KLASSEN, queryFn: () => rufe<{ klassen: KlasseZeile[] }>(api.api.laden.regalklassen.$get()) });
export function useRegalklasseAnlegen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: RegalklasseEingabe) => rufe<{ id: string }>(api.api.laden.regalklassen.$post({ json: d })), onSuccess: nach });
}
export function useRegalklasseAendern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string } & RegalklasseEingabe) => rufe(api.api.laden.regalklassen[":id"].$put({ param: { id: v.id }, json: v })), onSuccess: nach });
}

export const useRegale = () => useQuery({ queryKey: K_REGALE, queryFn: () => rufe<{ regale: RegalZeile[] }>(api.api.laden.regale.$get()) });
export const useRegal = (id: string) => useQuery({ queryKey: [...K_REGALE, id], queryFn: () => rufe<RegalDetail>(api.api.laden.regale[":id"].$get({ param: { id } })), enabled: !!id });
export const useFreieRegale = () => useQuery({ queryKey: [...K_REGALE, "frei"], queryFn: () => rufe<FreieRegale>(api.api.laden.regale.frei.$get()) });
export function useRegalAnlegen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: RegalEingabe) => rufe<{ id: string }>(api.api.laden.regale.$post({ json: d })), onSuccess: nach });
}
export function useRegalAendern() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (v: { id: string } & RegalEingabe) => rufe(api.api.laden.regale[":id"].$put({ param: { id: v.id }, json: v })), onSuccess: nach });
}
export function useRegaleMassen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: RegaleMassenEingabe) => rufe<{ angelegt: string[]; uebersprungen: string[] }>(api.api.laden.regale.massen.$post({ json: d })), onSuccess: nach });
}
export const useRegalFrei = (id: string | null, start: string | null, ende: string | null) =>
  useQuery({
    queryKey: [...K_REGALE, id, "frei", start, ende],
    queryFn: () => rufe<{ frei: boolean; konflikt: { nummer: string; start: string; ende: string | null } | null }>(api.api.laden.regale[":id"].frei.$get({ param: { id: id! }, query: { start: start!, ende: ende ?? undefined } })),
    enabled: !!id && !!start,
  });

export type PlanDaten = Awaited<ReturnType<typeof planLaden>>;
export const usePlan = () => useQuery({ queryKey: [...K_REGALE, "plan"], queryFn: () => rufe<PlanDaten>(api.api.laden.plan.$get()) });
export function usePlanSpeichern() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (e: PlanEingabe) => rufe(api.api.laden.plan.$put({ json: e })), onSuccess: () => void qc.invalidateQueries({ queryKey: K_REGALE }) });
}
/** Grundriss-Bild hochladen (File) oder entfernen (null). */
export function usePlanHintergrund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (datei: File | null) => {
      if (!datei) return rufe(api.api.laden.plan.hintergrund.$delete());
      const res = await fetch("/api/laden/plan/hintergrund", { method: "PUT", headers: { "content-type": datei.type }, body: datei, credentials: "same-origin" });
      if (!res.ok) throw new ApiFehler(((await res.json().catch(() => ({}))) as { fehler?: string }).fehler ?? "Upload fehlgeschlagen.", res.status);
      return res.json();
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: K_REGALE }),
  });
}
export const useBelegung = (von: string, bis: string, lage?: string) =>
  useQuery({ queryKey: ["laden", "belegung", von, bis, lage ?? ""], queryFn: () => rufe<Belegung>(api.api.laden.belegung.$get({ query: { von, bis, lage: lage || undefined } })), placeholderData: (alt) => alt });
